import {
  BooleanField,
  BooleanInput,
  BulkDeleteButton,
  ChipField,
  Create,
  Datagrid,
  DateField,
  DateTimeInput,
  Edit,
  List,
  Loading,
  Resource,
  SelectInput,
  SimpleForm,
  TextField,
  TextInput,
  useRecordContext,
} from "react-admin";
import { CrudPermissions } from "../../auth/crud-permissions";
import {
  ConversionFilterListResource,
  ConversionOutputStreamDestination,
  ConversionOutputStreamResource,
  OutputStreamStatsEntry,
  StartFrom,
} from "@arrowsup/platform-dtos";
import { useConversionFilters } from "../../../logic/conversions/use-conversion-filter";
import { useOutputStreamStats } from "../../../logic/conversions/use-output-stream-stats";
import OutputIcon from "@mui/icons-material/Output";
import { Box, Divider, Typography } from "@mui/material";
import { useWatch } from "react-hook-form";
import { omit } from "lodash";
import { createContext, ReactElement } from "react";
import {
  formatTimestamp,
  parseTimestamp,
} from "../../../logic/api/timestamp-codec";
import { TopHelpInfoText } from "../../help/top-help-info-text";
import { createStatsField } from "../shared/stats-field";

type OutputStreamStatsContextValue = {
  statsMap: Map<number, OutputStreamStatsEntry>;
  isLoading: boolean;
};

const OutputStreamStatsContext = createContext<OutputStreamStatsContextValue>({
  statsMap: new Map(),
  isLoading: true,
});

const StatsField = createStatsField(
  OutputStreamStatsContext,
  (entry, window) => {
    const stats = entry[window as keyof OutputStreamStatsEntry] as
      | { success: number; total: number }
      | undefined;
    if (!stats || stats.total === 0) return null;
    const allSuccess = stats.success === stats.total;
    return {
      label: `${Number(stats.success).toLocaleString()}/${Number(
        stats.total
      ).toLocaleString()}`,
      color: allSuccess ? "secondary" : "primary",
    };
  }
);

export const ConversionOutputStream = (perms: CrudPermissions) => {
  return (
    <Resource
      name="conversion-output-stream"
      key="conversion-output-stream"
      options={{
        label: "Output Streams",
      }}
      list={ConversionOutputStreamList(perms)}
      create={perms.create ? ConversionOutputStreamCreate() : undefined}
      edit={perms.update ? ConversionOutputStreamEdit() : undefined}
      icon={OutputIcon}
    />
  );
};

const ConversionOutputStreamListInner: React.FC<{
  perms: CrudPermissions;
}> = ({ perms }) => {
  const stats = useOutputStreamStats();

  return (
    <OutputStreamStatsContext.Provider value={stats}>
      <TopHelpInfoText text="Send filtered conversions to external destinations like Google Ads, Meta, or webhooks." />
      <List>
        <Datagrid
          rowClick={perms.update ? "show" : false}
          bulkActionButtons={perms.delete ? <BulkDeleteButton /> : false}
        >
          <TextField source="id" label="Stream ID" />
          <TextField source="description" label="Description" />
          <ChipField source="destination.name" label="Destination" />
          <TextField source="filter.name" label="Filter" />
          <ChipField source="startFrom" label="Start From" />
          <BooleanField source="enabled" />
          <BooleanField source="realtime" />
          <StatsField
            source="id"
            window="stats1d"
            label="1d"
            sortable={false}
          />
          <StatsField
            source="id"
            window="stats7d"
            label="7d"
            sortable={false}
          />
          <StatsField
            source="id"
            window="stats30d"
            label="30d"
            sortable={false}
          />
          <DateField source="createdAt" label="Created" />
          <DateField source="updatedAt" label="Last Updated" />
        </Datagrid>
      </List>
    </OutputStreamStatsContext.Provider>
  );
};

export const ConversionOutputStreamList = (perms: CrudPermissions) => {
  return <ConversionOutputStreamListInner perms={perms} />;
};

const FilterSelectInput: React.FC = () => {
  // Load saved filters.
  const { data, isPending, error } = useConversionFilters();

  if (isPending) {
    return <Loading />;
  }

  if (error) {
    throw new Error(`Error loading conversion filters: ${error.message}`);
  }

  return (
    <SelectInput
      source="filter.id"
      choices={(data.data as ConversionFilterListResource[]).map((filter) => ({
        id: filter.id,
        name: filter.name,
      }))}
      required
      fullWidth
    />
  );
};

const StartFromInput = () => {
  return (
    <SelectInput
      source="startFrom"
      choices={[
        { id: "beginning" satisfies StartFrom, name: "Beginning" },
        { id: "timestamp" satisfies StartFrom, name: "Timestamp" },
        { id: "end" satisfies StartFrom, name: "End" },
      ]}
      defaultValue={"end" satisfies StartFrom}
      required
      fullWidth
    />
  );
};

const FromTimestampInput = () => {
  // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
  const startFrom: StartFrom | undefined = useWatch({ name: "startFrom" });
  const record = useRecordContext();
  const isNew = record?.id === undefined;

  return (
    <DateTimeInput
      label="From Timestamp"
      source="fromTimestampMs"
      parse={parseTimestamp}
      format={formatTimestamp}
      helperText="The created timestamp (not event time) to start from, if 'Timestamp' is selected. Cannot be edited after the stream is created."
      fullWidth
      disabled={startFrom !== "timestamp" || !isNew}
    />
  );
};

const DestinationChooser: React.FC = () => {
  return (
    <SelectInput
      source="destination.name"
      choices={[
        {
          id: "email" satisfies ConversionOutputStreamDestination,
          name: "Email",
        },
        {
          id: "google-ads-conversion" satisfies ConversionOutputStreamDestination,
          name: "Google Ads Conversion",
        },
        {
          id: "google-ads-data-manager" satisfies ConversionOutputStreamDestination,
          name: "Google Ads (Data Manager API)",
        },
        {
          id: "meta-ads" satisfies ConversionOutputStreamDestination,
          name: "Meta Ads",
        },
        {
          id: "microsoft-ads-conversion" satisfies ConversionOutputStreamDestination,
          name: "Microsoft Ads Conversion",
        },
        {
          id: "webhook" satisfies ConversionOutputStreamDestination,
          name: "Webhook",
        },
      ]}
      required
      fullWidth
    />
  );
};

const NoDestinationSet: React.FC = () => {
  return <Typography>No destination set.</Typography>;
};

const EmailDestinationConfig: React.FC = () => {
  return (
    <TextInput
      label="Destination Email Address"
      source="destination.to"
      fullWidth
    />
  );
};

const WebhookDestinationConfig: React.FC = () => {
  return (
    <>
      <TextInput
        label="Destination URL"
        source="destination.webhookUrl"
        fullWidth
      />
    </>
  );
};

const MetaAdsDestinationConfig: React.FC = () => {
  return (
    <>
      <TextInput
        label="Meta Access Token"
        source="destination.accessToken"
        fullWidth
      />
      <TextInput label="Meta Pixel ID" source="destination.pixelId" fullWidth />
      <TextInput
        label="Meta Event Name"
        source="destination.eventName"
        fullWidth
      />
      <TextInput
        label="Meta Account ID"
        source="destination.accountId"
        fullWidth
      />
    </>
  );
};

const GoogleAdsConversionDestinationConfig: React.FC = () => {
  return (
    <>
      <TextInput
        label="Google Ads Conversion Action ID"
        source="destination.conversionActionId"
        fullWidth
      />
      <BooleanInput label="Enhanced" source="destination.enhanced" fullWidth />
      <TextInput
        label="Google Ads Customer ID"
        source="destination.customerId"
        fullWidth
      />
    </>
  );
};

const GoogleAdsDataManagerDestinationConfig: React.FC = () => {
  return (
    <>
      <TextInput
        label="Google Ads Conversion Action ID (numeric)"
        source="destination.conversionActionId"
        fullWidth
      />
      <BooleanInput label="Enhanced" source="destination.enhanced" fullWidth />
      <TextInput
        label="Google Ads Customer ID"
        source="destination.customerId"
        fullWidth
      />
    </>
  );
};

const MicrosoftAdsConversionDestinationConfig: React.FC = () => {
  return (
    <>
      <TextInput
        label="Microsoft Ads Conversion Name"
        source="destination.conversionName"
        fullWidth
      />
      <BooleanInput label="Enhanced" source="destination.enhanced" fullWidth />
      <TextInput
        label="Microsoft Ads Customer ID"
        source="destination.customerId"
        fullWidth
      />
    </>
  );
};

const DestinationConfig: React.FC = () => {
  // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
  const destinationName: ConversionOutputStreamDestination | undefined =
    useWatch({ name: "destination.name" });

  const configMap: Record<ConversionOutputStreamDestination, ReactElement> = {
    email: <EmailDestinationConfig />,
    webhook: <WebhookDestinationConfig />,
    "meta-ads": <MetaAdsDestinationConfig />,
    "google-ads-conversion": <GoogleAdsConversionDestinationConfig />,
    "google-ads-data-manager": <GoogleAdsDataManagerDestinationConfig />,
    "microsoft-ads-conversion": <MicrosoftAdsConversionDestinationConfig />,
  };

  return (
    <Box sx={{ width: "100%" }}>
      <Divider sx={{ pb: 2 }}>Destination Config</Divider>
      {configMap[destinationName ?? "email"] ?? <NoDestinationSet />}
    </Box>
  );
};

const RealtimeInput: React.FC = () => {
  return (
    <BooleanInput
      label="Realtime Processing"
      source="realtime"
      helperText="If this stream should process conversions as they arrive.  This is taxing on the system. If disabled, events are batched and processed periodically."
      fullWidth
    />
  );
};

export const ConversionOutputStreamCreate = () => {
  return (
    <Create
      redirect="list"
      transform={(os: ConversionOutputStreamResource) => ({
        ...os,
      })}
    >
      <SimpleForm>
        <FilterSelectInput />
        <TextInput source="description" fullWidth />
        <StartFromInput />
        <FromTimestampInput />
        <BooleanInput fullWidth source="enabled" />
        <RealtimeInput />
        <DestinationChooser />
        <DestinationConfig />
      </SimpleForm>
    </Create>
  );
};

export const ConversionOutputStreamEdit = () => {
  return (
    <Edit
      redirect="list"
      transform={(os: ConversionOutputStreamResource) => {
        return {
          ...omit(os, ["createdAt", "updatedAt"]),
          filter: { id: os.filter.id },
        };
      }}
    >
      <SimpleForm>
        <FilterSelectInput />
        <TextInput source="description" fullWidth />
        <StartFromInput />
        <FromTimestampInput />
        <BooleanInput fullWidth source="enabled" />
        <RealtimeInput />
        <DestinationChooser />
        <DestinationConfig />
      </SimpleForm>
    </Edit>
  );
};
